import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { DialogShell } from '../../../components/DialogShell';
import { downloadDataExportFile } from '../../../data/export/browserFile';
import type { VectorExportFormat, VectorExportScope } from '../../../data/export/model';
import { planVectorDataExport } from '../../../data/export/planning';
import { prepareVectorDataExport } from '../../../data/export/prepare';
import { useCapability } from '../../../features/useCapability';
import type { LayerId } from '../../../project/model';
import { useAppStore } from '../../state/useAppStore';

interface ExportDataDialogProps {
  readonly layerId: LayerId;
  readonly onClose: () => void;
}

export function ExportDataDialog({ layerId, onClose }: ExportDataDialogProps) {
  const project = useAppStore((state) => state.project);
  const selection = useAppStore((state) => state.shell.selectedFeatures);
  const canExportSelected = useCapability('data.exportSelected');
  const layer = project.layers.find((candidate) => candidate.id === layerId);
  const [format, setFormat] = useState<VectorExportFormat>('geojson');
  const [scope, setScope] = useState<VectorExportScope>('all');
  const [includeJoinedFields, setIncludeJoinedFields] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formatRef = useRef<HTMLSelectElement | null>(null);
  const titleId = useId();
  const selectedDescriptionId = useId();
  const selectedFeatureCount = layer
    ? selection.filter(
        (feature) => feature.layerId === layer.id && feature.sourceId === layer.sourceId,
      ).length
    : 0;
  const selectedScopeAvailable = canExportSelected && selectedFeatureCount > 0;
  const planResult = useMemo(() => {
    if (!layer) return null;
    try {
      return {
        ok: true as const,
        plan: planVectorDataExport(project.sources, layer, selection, {
          scope,
          includeJoinedFields,
        }),
      };
    } catch (cause) {
      return {
        ok: false as const,
        message: cause instanceof Error ? cause.message : 'The export could not be prepared.',
      };
    }
  }, [includeJoinedFields, layer, project.sources, scope, selection]);

  useEffect(() => {
    if (!layer) onClose();
  }, [layer, onClose]);

  useEffect(() => {
    if (scope === 'selected' && !selectedScopeAvailable) {
      // Selection may be cleared outside this dialog; normalize its controlled scope afterward.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setScope('all');
    }
  }, [scope, selectedScopeAvailable]);

  if (!layer) return null;
  const plan = planResult?.ok ? planResult.plan : null;
  const featureCount = plan?.featureIndices.length ?? 0;
  const fieldCount = plan?.fields.length ?? 0;
  const planError = error ?? (!planResult?.ok ? planResult?.message : null);
  const exportDisabled =
    preparing ||
    !plan ||
    (scope === 'selected' && !selectedScopeAvailable) ||
    (format === 'csv' && fieldCount === 0);

  return (
    <DialogShell
      className="new-map-dialog export-data-dialog"
      closeOnEscape={!preparing}
      initialFocusRef={formatRef}
      onClose={onClose}
      titleId={titleId}
    >
      <h2 id={titleId}>Export data</h2>
      <div className="export-data-layer">
        <span>Layer</span>
        <strong>{layer.name}</strong>
      </div>
      <label className="export-data-control">
        <span>Format</span>
        <select
          aria-label="Export format"
          onChange={(event) => setFormat(event.target.value as VectorExportFormat)}
          ref={formatRef}
          value={format}
        >
          <option value="geojson">GeoJSON</option>
          <option value="csv">CSV</option>
        </select>
      </label>
      <fieldset className="export-data-scope">
        <legend>Scope</legend>
        <label>
          <input
            checked={scope === 'all'}
            name="export-scope"
            onChange={() => setScope('all')}
            type="radio"
            value="all"
          />
          <span>All features</span>
        </label>
        <label>
          <input
            aria-describedby={!canExportSelected ? selectedDescriptionId : undefined}
            checked={scope === 'selected'}
            disabled={!selectedScopeAvailable}
            name="export-scope"
            onChange={() => setScope('selected')}
            type="radio"
            value="selected"
          />
          <span>Selected features</span>
          {!canExportSelected ? (
            <span className="pro-tag" id={selectedDescriptionId}>
              Pro
            </span>
          ) : null}
        </label>
      </fieldset>
      {layer.joins.length > 0 ? (
        <fieldset className="export-data-fields">
          <legend>Fields</legend>
          <label>
            <input
              checked={includeJoinedFields}
              onChange={(event) => setIncludeJoinedFields(event.target.checked)}
              type="checkbox"
            />
            <span>Include joined fields</span>
          </label>
        </fieldset>
      ) : null}
      <p className="export-data-summary" role="status">
        {scope === 'selected' && featureCount === 0
          ? '0 selected features'
          : `${featureCount.toLocaleString()} ${featureCount === 1 ? 'feature' : 'features'} · ${fieldCount.toLocaleString()} ${fieldCount === 1 ? 'field' : 'fields'}`}
      </p>
      {planError ? (
        <p className="style-notice" role="alert">
          {planError}
        </p>
      ) : null}
      <div className="new-map-dialog-actions">
        <button disabled={preparing} onClick={onClose} type="button">
          Cancel
        </button>
        <button
          className="primary-action"
          disabled={exportDisabled}
          onClick={async () => {
            if (!plan || exportDisabled) return;
            setPreparing(true);
            setError(null);
            try {
              downloadDataExportFile(await prepareVectorDataExport(plan, format));
              onClose();
            } catch {
              setError(
                format === 'geojson'
                  ? 'Could not prepare GeoJSON export.'
                  : 'Could not prepare CSV export.',
              );
              setPreparing(false);
            }
          }}
          type="button"
        >
          {preparing ? 'Preparing…' : 'Export'}
        </button>
      </div>
    </DialogShell>
  );
}
